const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'remate_token';

/**
 * Build a query string from a params object, omitting undefined/null values.
 * @param {Object} params
 * @returns {string}
 */
function buildQuery(params) {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `?${qs}` : '';
}

/**
 * Returns auth headers with the stored JWT token.
 * @returns {Object}
 */
function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Authenticated fetch wrapper. Throws on 401 with a special flag to trigger logout.
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...options.headers },
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    const err = new Error('Session expired. Please log in again.');
    err.isUnauthorized = true;
    throw err;
  }
  return res;
}

/**
 * Authenticate and return a JWT token.
 * @param {string} username
 * @param {string} password
 * @returns {Promise<string>} JWT token
 */
export async function loginUser(username, password) {
  const res = await fetch(`${BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || 'Invalid credentials');
  }
  const json = await res.json();
  return json.token;
}

/**
 * Fetch the list of configured databases.
 * @returns {Promise<Array<{index: number, label: string, isV3: boolean}>>}
 */
export async function fetchDatabases() {
  const res = await apiFetch(`${BASE_URL}/api/databases`);
  if (!res.ok) throw new Error('Failed to fetch databases');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch auctions scheduled for the current week.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<Array>}
 */
export async function fetchWeeklyAuctions(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/weekly-auctions${buildQuery({ db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch weekly auctions');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch per-tenant stats for a given month.
 * @param {string} [month] - Month in YYYY-MM format. Defaults to current month.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<Array>}
 */
export async function fetchTenantStats(month, dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/tenant-stats${buildQuery({ month, db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch tenant stats');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch global summary totals for a given month.
 * @param {string} [month] - Month in YYYY-MM format. Defaults to current month.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<Object>}
 */
export async function fetchSummary(month, dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/summary${buildQuery({ month, db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch summary');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch per-tenant stats for a whole year.
 * @param {number|string} year - Year in YYYY format.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<Array>}
 */
export async function fetchYearlyTenantStats(year, dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/tenant-stats${buildQuery({ year, db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch yearly tenant stats');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch global summary totals for a whole year, including tenants created that year.
 * @param {number|string} year - Year in YYYY format.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<Object>}
 */
export async function fetchYearlySummary(year, dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/summary${buildQuery({ year, db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch yearly summary');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch the years that have auctions (newest first), for the yearly filter.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<number[]>}
 */
export async function fetchAvailableYears(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/years${buildQuery({ db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch years');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch all upcoming auctions for the general showroom.
 * @param {number} [dbIndex]
 * @returns {Promise<Array>}
 */
export async function fetchShowroom(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/showroom${buildQuery({ db: dbIndex })}`);
  if (!res.ok) throw new Error('Error al cargar la sala de exhibición');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch individual lots from auctions currently in exhibition
 * (statuses: postulations_open, postulations, closed, preparing_to_broadcast, broadcasting).
 * @param {number} [dbIndex]
 * @returns {Promise<Array>}
 */
export async function fetchExhibitionLots(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/showroom/lots${buildQuery({ db: dbIndex })}`);
  if (!res.ok) throw new Error('Error al cargar la exhibición de lotes');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch auctions for the past week, current week, and next week.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<Array>}
 */
export async function fetchThreeWeekAuctions(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/three-week-auctions${buildQuery({ db: dbIndex })}`);
  if (!res.ok) throw new Error('Failed to fetch three-week auctions');
  const json = await res.json();
  return json.data;
}

/**
 * Fetch all enabled tenants with their broadcast channels.
 * @param {number} [dbIndex]
 * @returns {Promise<Array>}
 */
export async function fetchCanales(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/canales${buildQuery({ db: dbIndex })}`);
  if (!res.ok) throw new Error('Error al cargar los canales');
  const json = await res.json();
  return json.data;
}

/**
 * Manually trigger the weekly auction summary Slack notification.
 * @param {number} [dbIndex] - 1-based database index.
 * @returns {Promise<void>}
 */
export async function sendSlackSummary(dbIndex) {
  const res = await apiFetch(`${BASE_URL}/api/dashboard/send-slack-summary${buildQuery({ db: dbIndex })}`, {
    method: 'POST',
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || 'Failed to send Slack summary');
  }
}
